import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, X as XIcon, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingPage } from '@/components/ui/loading';
import { cn } from '@/lib/utils';
import { QrScanner } from '@/features/setup/components/qr-scanner';
import {
  lookupDeviceForScan,
  commitDeviceSetup,
  SetupDeviceError,
  type ScanLookupResult,
} from '@/features/setup/utils/setup-device-without-transmission';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { getBuilding } from '@/features/buildings/api/buildings-api';
import { getUnits } from '@/features/units/api/units-api';
import { useDataCache } from '@/store/data-cache-context';
import {
  computeNextAvailableUnit,
  nextUnitAfter as nextUnitAfterUtil,
} from '@/features/setup/utils/unit-numbering';
import type { Building, Unit } from '@/types/api';

const COOLDOWN_MS = 1500;

// Lowest positive integer not in use by any existing unit. Mirrors the
// panel's helper so auto-increment works the same way on the companion
// phone. See `@/features/setup/utils/unit-numbering` for full semantics.
function computeNextBuildingUnit(units: Unit[]): string {
  return computeNextAvailableUnit(units.map((u) => ({ number: u.unitNumber })));
}

function nextUnitAfter(submitted: string, units: Unit[]): string {
  return nextUnitAfterUtil(submitted, units.map((u) => ({ number: u.unitNumber })));
}

interface Scanned {
  serial: string;
  unit: string;
  reassigned: boolean;
  at: number;
}

export function PhoneScannerPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const propertyId = params.get('propertyId') ?? '';
  const buildingId = params.get('buildingId') ?? '';
  // When the scanner was opened from Setup Mode (mobile in-flow tap or the
  // desktop pair-phone QR that's already in Setup Mode context), Done / X
  // should return the phone to Setup Mode on the property page rather than
  // the bare property view. Navigate directly to `?mode=setup` (the
  // canonical Setup Mode signal PropertyPortal already keys off) and
  // forward buildingId so the panel rehydrates with the right building
  // selected instead of the building-picker pre-screen.
  const fromSetupMode = params.get('from') === 'setup-mode';
  const exitTarget = fromSetupMode
    ? `/properties/${propertyId}?mode=setup${buildingId ? `&buildingId=${buildingId}` : ''}`
    : `/properties/${propertyId}`;
  const { invalidate } = useDataCache();
  // Meter type is no longer chosen here. The decoder lambda classifies
  // every uplink and the API auto-corrects the device's stored type;
  // SALT pulse-encoder calibration is collected from the device detail
  // page after install.

  const [building, setBuilding] = useState<Building | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);

  const [unitOverride, setUnitOverride] = useState<string | null>(null);
  const seededUnit = useMemo(() => computeNextBuildingUnit(units), [units]);
  const unitNumber = unitOverride ?? seededUnit;
  // Auto-mode is whether the unit field will *advance* after each scan.
  // Tracked separately from `unitOverride` because the previous logic
  // treated "unitOverride !== null" as the manual-mode signal — but
  // handleScan itself sets unitOverride to nextUnitAfter(...) on every
  // commit, so the auto-advance path was indistinguishable from the
  // user-typed-something path. The pill flipped to "Resume Auto" right
  // after a successful scan even though auto-increment was very much
  // still active. Toggling autoMode explicitly fixes the visual + the
  // "is the field advancing on its own" semantics.
  const [autoMode, setAutoMode] = useState(true);

  const [scanned, setScanned] = useState<Scanned[]>([]);
  const [lastResult, setLastResult] = useState<
    | { kind: 'ok'; serial: string; unit: string; reassigned: boolean; isSalt: boolean }
    | { kind: 'err'; message: string }
    | null
  >(null);
  // Reassignment confirm gate. When non-null, the camera ignores incoming
  // scans (via coolingRef set during open) and the modal asks the installer
  // to confirm. On confirm we re-run lookup to detect any concurrent change
  // before committing.
  const [pendingConfirm, setPendingConfirm] = useState<
    | (Extract<ScanLookupResult, { kind: 'needs-confirm' }> & { targetUnit: string })
    | null
  >(null);
  const [confirmInFlight, setConfirmInFlight] = useState(false);
  const cooldownRef = useRef<number | null>(null);
  // Server-confirmed-success banner auto-dismiss. Cleared on each new
  // success so back-to-back scans restart the 2.5s window cleanly.
  const successDismissRef = useRef<number | null>(null);
  // `cooling` is kept in a ref (not state) so handleScan's closure can read
  // the latest value without being invalidated on every scan. Re-creating
  // handleScan would re-run QrScanner's useEffect and restart the camera
  // stream — which drops frames between scans.
  const coolingRef = useRef(false);
  const lastSerialRef = useRef<{ value: string; at: number } | null>(null);
  // Latest unit + units snapshot, read by handleScan via refs so the
  // callback stays reference-stable across renders.
  const unitRef = useRef('');
  const unitsRef = useRef<Unit[]>([]);
  // Mirror autoMode in a ref so finalizeCommit (whose useCallback deps
  // intentionally don't include autoMode — we want the function to
  // stay reference-stable across renders) reads the latest value.
  const autoModeRef = useRef(true);
  useEffect(() => { unitRef.current = unitNumber; }, [unitNumber]);
  useEffect(() => { unitsRef.current = units; }, [units]);
  useEffect(() => { autoModeRef.current = autoMode; }, [autoMode]);

  // Load building + units once.
  useEffect(() => {
    if (!buildingId) {
      setLoadErr('Pairing link is missing a building — ask your PC to re-pair.');
      return;
    }
    let cancelled = false;
    Promise.all([getBuilding(buildingId), getUnits({ buildingId, perPage: 200 })])
      .then(([buildingRes, unitsRes]) => {
        if (cancelled) return;
        if (buildingRes.success && buildingRes.data) setBuilding(buildingRes.data);
        setUnits(unitsRes.items);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadErr('Could not load this pairing context. Try re-pairing from the PC.');
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId]);

  // Shared commit path — runs after the lookup phase has classified the
  // scan, and again after the user confirms a reassignment. `at` is the
  // timestamp captured when the user-visible scan happened so the order in
  // the recent-scans list reflects scan time, not commit time.
  const finalizeCommit = useCallback(
    async (lookup: ScanLookupResult, targetUnit: string, at: number) => {
      const result = await commitDeviceSetup({
        lookup,
        buildingId,
        unitNumber: targetUnit,
      });
      setScanned((prev) => [
        {
          serial: result.device.serialNumber ?? lookup.serial,
          unit: result.unit.unitNumber,
          reassigned: result.reassigned,
          at,
        },
        ...prev,
      ].slice(0, 8));
      setLastResult({
        kind: 'ok',
        serial: result.device.serialNumber ?? lookup.serial,
        unit: result.unit.unitNumber,
        reassigned: result.reassigned,
        isSalt: result.device.deviceType === 'SALTTransmitter',
      });
      // Tactile confirmation on phones that support it. Feature-detect
      // before calling — older browsers and most desktops don't expose it.
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate(50); } catch { /* non-fatal */ }
      }
      // Auto-dismiss the success banner after 2.5s. Restart on each new
      // success so a fast streak of pairs gets the freshest banner.
      if (successDismissRef.current) window.clearTimeout(successDismissRef.current);
      successDismissRef.current = window.setTimeout(() => {
        setLastResult((prev) => (prev?.kind === 'ok' ? null : prev));
      }, 2500);
      // Only auto-advance when the user is in auto-mode. If they
      // explicitly tapped the pill to disable it, leave their typed
      // unit alone so they can scan multiple meters into the same unit.
      if (autoModeRef.current) {
        setUnitOverride(nextUnitAfter(targetUnit, unitsRef.current));
      }
      invalidate(['devices', 'units', 'properties']);
    },
    [buildingId, invalidate],
  );

  const handleScan = useCallback(
    async (raw: string) => {
      const serial = raw.replace(/^SN\s*/i, '').trim().toUpperCase();
      if (!serial) return;
      if (coolingRef.current) return;

      // Debounce the SAME QR held in frame — BarcodeDetector fires
      // repeatedly on each frame while the code is visible.
      const now = Date.now();
      const last = lastSerialRef.current;
      if (last && last.value === serial && now - last.at < 2000) return;
      lastSerialRef.current = { value: serial, at: now };

      const unit = unitRef.current.trim();
      if (!unit) {
        setLastResult({ kind: 'err', message: 'Enter a unit number first.' });
        return;
      }

      coolingRef.current = true;
      try {
        const lookup = await lookupDeviceForScan({
          buildingId,
          serialNumber: serial,
          unitNumber: unit,
        });

        if (lookup.kind === 'needs-confirm') {
          // Hand the camera a long cooldown — coolingRef stays true while
          // the modal is open. We clear it on confirm/cancel.
          setPendingConfirm({ ...lookup, targetUnit: unit });
          return;
        }

        await finalizeCommit(lookup, unit, now);
      } catch (err) {
        if (err instanceof SetupDeviceError) {
          setLastResult({ kind: 'err', message: err.message });
        } else {
          const msg =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            (err instanceof Error ? err.message : 'Something went wrong — try again.');
          setLastResult({ kind: 'err', message: msg });
        }
      } finally {
        // Skip the cooldown release while the modal is open — pendingConfirm
        // owns the "scanner is paused" semantics until the installer answers.
        if (!pendingConfirm) {
          if (cooldownRef.current) window.clearTimeout(cooldownRef.current);
          cooldownRef.current = window.setTimeout(() => { coolingRef.current = false; }, COOLDOWN_MS);
        }
      }
    },
    [buildingId, finalizeCommit, pendingConfirm],
  );

  // User clicked "Reassign" in the confirm modal. Re-run the lookup so a
  // concurrent change (another installer reassigned the same serial while
  // this modal was open) is detected before we commit. If the source unit
  // has changed since the modal opened, abort with a "scan again" message
  // rather than silently clobbering the new state.
  async function handleConfirmReassign() {
    if (!pendingConfirm) return;
    setConfirmInFlight(true);
    const at = Date.now();
    try {
      const fresh = await lookupDeviceForScan({
        buildingId,
        serialNumber: pendingConfirm.serial,
        unitNumber: pendingConfirm.targetUnit,
      });
      if (fresh.kind !== 'needs-confirm' || fresh.device.unitId !== pendingConfirm.device.unitId) {
        setLastResult({
          kind: 'err',
          message: 'Device assignment changed since you scanned. Please scan again.',
        });
        setPendingConfirm(null);
        return;
      }
      await finalizeCommit(fresh, pendingConfirm.targetUnit, at);
      setPendingConfirm(null);
    } catch (err) {
      const msg = err instanceof SetupDeviceError
        ? err.message
        : ((err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
           (err instanceof Error ? err.message : 'Something went wrong — try again.'));
      setLastResult({ kind: 'err', message: msg });
      setPendingConfirm(null);
    } finally {
      setConfirmInFlight(false);
      if (cooldownRef.current) window.clearTimeout(cooldownRef.current);
      cooldownRef.current = window.setTimeout(() => { coolingRef.current = false; }, COOLDOWN_MS);
    }
  }

  function handleCancelReassign() {
    setPendingConfirm(null);
    if (cooldownRef.current) window.clearTimeout(cooldownRef.current);
    cooldownRef.current = window.setTimeout(() => { coolingRef.current = false; }, COOLDOWN_MS);
  }

  useEffect(() => () => {
    if (cooldownRef.current) window.clearTimeout(cooldownRef.current);
    if (successDismissRef.current) window.clearTimeout(successDismissRef.current);
  }, []);

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-black text-white">
        <div className="flex flex-col items-center text-center gap-3">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
          <p className="text-sm">{loadErr}</p>
          <Button variant="outline" onClick={() => navigate('/setup/pair', { replace: true })}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!building) return <LoadingPage />;

  const buildingLabel = building.propertyName
    ? `${building.propertyName} / ${building.name}`
    : building.name;

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-start justify-between gap-3 p-4 bg-black/70 backdrop-blur z-10">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-white/60">Scanning into</p>
          <p className="text-sm font-semibold truncate">{buildingLabel}</p>
          <p className="text-xs text-white/60 mt-0.5">
            {scanned.length} meter{scanned.length === 1 ? '' : 's'} added
          </p>
        </div>
        {/* Scanner shell is always-dark (`bg-black text-white`) regardless
            of the app theme, so the Done button styles its own colors with
            translucent white tokens rather than inheriting the app theme.
            Using `variant="outline"` previously caused a white-on-white bug
            in light theme: the variant injected `bg-white text-near-black`
            via theme tokens, which clobbered our explicit `text-white`. We
            use `variant="ghost"` (no `bg-*` in light mode) and explicitly
            override every theme-dependent utility — including dark-mode
            ones — so the button looks identical in both themes. */}
        <Button
          variant="ghost"
          size="sm"
          className="bg-white/10 text-white dark:text-white border border-white/30 hover:bg-white/20 dark:hover:bg-white/20 active:bg-white/30 flex-shrink-0"
          onClick={() => navigate(exitTarget, { replace: true })}
        >
          <XIcon className="w-4 h-4" /> Done
        </Button>
      </div>

      {/* Camera fills remaining space. embedded=true skips the default
          modal-fullscreen takeover on mobile so our own top bar and bottom
          panel stay visible and interactive on top of the camera. */}
      <div className="flex-1 relative min-h-0">
        <QrScanner
          continuous
          embedded
          onScan={handleScan}
          onClose={() => navigate(exitTarget, { replace: true })}
        />
      </div>

      {/* Unit input + live feedback — sits above the camera */}
      <div className="absolute left-0 right-0 bottom-0 p-4 space-y-3 bg-gradient-to-t from-black via-black/80 to-transparent">
        {/* Success banner — only renders after server-confirmed pair (no
            optimistic flash). Larger type, larger icon, and a fade-in lift
            so installers on a noisy job site see the confirmation without
            looking at the screen. Auto-dismisses after 2.5s via the timer
            in `finalizeCommit`. The aria-live region announces "Paired
            serial X to Unit Y" so screen-reader users get parity. */}
        {lastResult?.kind === 'ok' && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="norgas-flash-in flex items-center gap-2 text-base font-medium bg-success-600/90 text-white rounded-lg px-4 py-3"
          >
            <CheckCircle2 className="w-6 h-6 flex-shrink-0" aria-hidden="true" />
            <span>
              {lastResult.reassigned ? 'Reassigned' : 'Paired'}{' '}
              <span className="font-mono">{lastResult.serial}</span>
              {lastResult.isSalt && (
                <span className="ml-1 inline-flex items-center rounded-md bg-white/20 px-1.5 py-0.5 text-xs font-semibold tracking-wide">
                  SALT
                </span>
              )}
              {' '}→ {lastResult.unit}
            </span>
          </div>
        )}
        {lastResult?.kind === 'err' && (
          <div className="flex items-center gap-2 text-sm bg-error-600/90 text-white rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{lastResult.message}</span>
          </div>
        )}
        {/* Unit field + auto-state pill. The pill is more than a button:
            it's a live indicator. Pulsing green dot signals auto-increment
            is active. When the installer types a manual unit the pill flips
            to a flat "Resume Auto" affordance. State is announced via
            aria-pressed + aria-describedby on the input. */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Input
              label=""
              value={unitNumber}
              onChange={(e) => {
                // Typing in the field is the explicit "I want to control
                // the unit" signal — flip out of auto-mode so the next
                // scan doesn't auto-advance off whatever the user just
                // typed.
                setAutoMode(false);
                setUnitOverride(e.target.value);
              }}
              placeholder={!unitNumber ? 'Type first unit, e.g. 101' : 'Unit'}
              className="text-center text-lg"
              aria-describedby="auto-state-helper"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              if (autoMode) {
                // Toggle OFF: keep the current unit value visible so the
                // user can edit from there instead of losing their place.
                setAutoMode(false);
              } else {
                // Toggle ON: reset to the seeded next unit so the auto-
                // advance starts from a clean state.
                setAutoMode(true);
                setUnitOverride(null);
              }
            }}
            aria-pressed={autoMode}
            aria-label={autoMode ? 'Auto-increment is on — tap to turn off' : 'Resume auto-increment'}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-all flex-shrink-0',
              'focus:outline-none focus:ring-2 focus:ring-white/40',
              autoMode
                ? 'bg-success-500/15 border-success-400/40 text-success-100 hover:bg-success-500/25 active:scale-95'
                : 'bg-white/5 border-white/30 text-white hover:bg-white/15 active:scale-95',
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'w-1.5 h-1.5 rounded-full transition-all',
                autoMode
                  ? 'bg-success-400 animate-pulse shadow-[0_0_6px_rgba(74,222,128,0.85)]'
                  : 'bg-white/40',
              )}
            />
            {autoMode ? 'Auto' : 'Resume Auto'}
          </button>
        </div>
        <p id="auto-state-helper" className="text-xs text-white/60 text-center">
          {autoMode
            ? 'Each scan auto-submits and the unit increments.'
            : 'Manual unit number — tap "Resume Auto" to return to auto-increment.'}
        </p>
      </div>

      {/* Reassignment confirm — only fires when the scanned serial is
          currently assigned to a *different* unit. Camera scans are paused
          while open (coolingRef stays true). No auto-cancel: the modal
          waits for an explicit answer so screen-reader installers and
          installers who walk away aren't surprised by a silent clobber
          either way. */}
      <ConfirmDialog
        isOpen={pendingConfirm !== null}
        title="Reassign meter to a different unit?"
        description={
          pendingConfirm ? (
            <div className="space-y-2">
              <p>
                Serial <span className="font-mono font-semibold">{pendingConfirm.serial}</span> is currently assigned to{' '}
                <strong>
                  {pendingConfirm.sourceBuildingName ? `${pendingConfirm.sourceBuildingName} · ` : ''}
                  {pendingConfirm.sourceUnitNumber ?? '—'}
                </strong>.
              </p>
              <p>
                Reassign to <strong>{pendingConfirm.targetUnit}</strong>?
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This usually only happens when a meter is being moved, or a different QR was scanned by mistake.
              </p>
            </div>
          ) : null
        }
        confirmLabel="Reassign"
        cancelLabel="Cancel"
        variant="primary"
        onConfirm={handleConfirmReassign}
        onCancel={handleCancelReassign}
        isLoading={confirmInFlight}
      />
    </div>
  );
}
